import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { PatientEventLogsFilter, ToastService, UpdateAnomalyStatus } from '../../core';
import { DiaryService } from '../../services';
import { BaseComponent, PaginationComponent, SortEvent } from '../../shared';
import { HistoryFiltersComponent } from '../history-filters';
import { HistoryTableComponent } from '../history-table';


/**
 * Component for anomaly logs table in patient history page. 
 */
@Component({
	selector: 'tsp-anomaly-table',
	imports: [
		PaginationComponent,
		HistoryTableComponent,
		HistoryFiltersComponent
	],
	templateUrl: './anomaly-table.component.html',
	changeDetection: ChangeDetectionStrategy.Eager
})
export class AnomalyTableComponent extends BaseComponent {

	private readonly diarySrv = inject(DiaryService);
	private readonly toastSrv = inject(ToastService);
	
	isReadonly = input.required<boolean>();

	filters = this.diarySrv.anomalyFilters;
	sortOrder = this.diarySrv.anomalySortOrder;
	anomalies = this.diarySrv.anomalies;

	collectionSize = computed(() => this.diarySrv.anomalyCount());
	pageNumber: number = 1;
	pageSize: number = 10;

	isUnreadBtnVisible = computed(() => this.filters()?.other);
	isReadBtnDisabled = computed(() => this.anomalies()?.every(i => !i.isSelected));


	/**
	 * Update anomalies list according to filters.
	 */
	updateList(filters: PatientEventLogsFilter | undefined): void {
		this.filters.set(filters ? { ...filters, page: 1, top: 10 } : { ...this.diarySrv.DEFAULT_ANOMALY_FILTERS });
		this.pageNumber = 1;
		this.pageSize = 10;
		this._search();
	}

	/**
	 * Update list by page number.
	 */
	updatePageNumber(): void {
		this.filters.update(f => ({ ...f, page: this.pageNumber }));
		this._search();
	}

	/**
	 * Update list by page size.
	 */
	updatePageSize(): void {
		this.filters.update(f => ({ ...f, top: this.pageSize }));
		this._search();
	}

	/**
	 * Sort list according to sort order.
	 */
	sort(order: SortEvent): void {
		this.filters.update(f => ({
			...f,
			sortOrder: { name: order.column, desc: order.direction === 'desc' },
			page: 1,
			top: 10
		}));
		this.pageNumber = 1;
		this.pageSize = 10;
		this._search();
	}

	private _search(): void {
		if (!this.collectionSize() || Math.ceil(this.collectionSize() / this.pageSize) >= this.pageNumber) {
			this.diarySrv.searchAnomalies().subscribe({
				error: () => this.toastSrv.showError(this.labels().HISTORY_FILTERS_COMPONENT_SEARCH_ERROR)
			});
		}
	}

	/**
	 * Set the selected items as unread.
	 */
	unread(): void {
		this._updateStatus(false);
	}

	/**
	 * Set the selected items as read.
	 */
	read(): void {
		this._updateStatus(true);
	}

	private _updateStatus(isRead: boolean): void {
		const items = this.anomalies().filter(a => a.isSelected).map(a => ({ id: a.id, isRead: isRead } as UpdateAnomalyStatus));
		this.diarySrv.changeReadStatus(items).subscribe({
			next: () => {
				this.toastSrv.showSuccess(this.labels().HISTORY_FILTERS_COMPONENT_READ_SUCCESS);
				this.updateList(this.filters());
			},
			error: () => this.toastSrv.showError(this.labels().HISTORY_FILTERS_COMPONENT_READ_ERROR)
		});
	}

}
